package com.iregained.site.components.sections.product.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.FaqIconStyle
import com.iregained.site.components.sections.product.styles.FaqRowHoverStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Slide Right
@Composable
private fun Modifier.staggeredSlideRight(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else (-40).px).transition(
            Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun ProductFaqSection(data: ProductFaqSectionData = defaultProductFaqData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= com.iregained.site.utils.Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "product-faq-section"
    var isVisible by remember { mutableStateOf(false) }

    if (data.faqs.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {
                Column(modifier = Modifier.fillMaxWidth().maxWidth(800.px)) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.fillMaxWidth().margin(bottom = 40.px).staggeredSlideRight(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    var expandedIndex by remember { mutableStateOf(-1) }

                    data.faqs.forEachIndexed { index, faq ->
                        if (faq.question.isNotBlank() && faq.answer.isNotBlank()) {
                            key(faq.question) {
                                Box(
                                    modifier = Modifier.fillMaxWidth()
                                        .staggeredSlideRight(isVisible, 150 + (index * 150))
                                ) {
                                    FaqAccordion(
                                        question = faq.question,
                                        answer = faq.answer,
                                        isExpanded = expandedIndex == index,
                                        onClick = { expandedIndex = if (expandedIndex == index) -1 else index })
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FaqAccordion(question: String, answer: String, isExpanded: Boolean, onClick: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Column(modifier = Modifier.fillMaxWidth().borderBottom(1.px, LineStyle.Solid, palette.custom.divider)) {
        Row(
            modifier = FaqRowHoverStyle.toModifier().fillMaxWidth().cursor(Cursor.Pointer)
                .padding(topBottom = 20.px, leftRight = 10.px).onClick { onClick() },
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            SpanText(
                text = question,
                modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold).fontSize(20.px)
            )
            SpanText(
                text = "+",
                modifier = FaqIconStyle.toModifier().color(palette.brand.primary).fontSize(28.px)
                    .rotate(if (isExpanded) 45.deg else 0.deg)
            )
        }
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Grid).styleModifier {
                property("grid-template-rows", if (isExpanded) "1fr" else "0fr"); property(
                "transition",
                "grid-template-rows 0.3s ease-out"
            )
            }.toAttrs()
        ) {
            Div(attrs = Modifier.fillMaxWidth().minHeight(0.px).overflow(Overflow.Hidden).toAttrs()) {
                Div(
                    attrs = Modifier.display(DisplayStyle.Block).color(palette.custom.darkText).lineHeight(1.6)
                        .padding(bottom = 20.px, leftRight = 10.px)
                        .toAttrs { ref { htmlElement -> htmlElement.innerHTML = answer; onDispose {} } })
            }
        }
    }
}

data class FaqData(val question: String, val answer: String)
data class ProductFaqSectionData(
    val sectionId: String? = null, val sectionHeader: String, val faqs: List<FaqData>
)

val defaultProductFaqData = ProductFaqSectionData(
    sectionId = "product-faq-section", sectionHeader = "FREQUENTLY ASKED QUESTIONS", faqs = listOf(
        FaqData(
            "How does it work?",
            "The MyHand<sup>®</sup> System pairs a precision mechatronic device with interactive digital neurotherapeutics to encourage high repetition of functional grasps."
        ), FaqData(
            "What is the intended use?",
            "It is intended to be used under the direction of a licensed rehabilitation professional to assist in the recovery of hand function in patients with neurological or musculoskeletal conditions."
        ), FaqData(
            "What purchasing pathways are available for clinics and home consumers?",
            "We keep our Customer’s requirements at the forefront and offer various acquisition paths according to their requirements. Facilities with approved capital budgets can secure CapEx ownership at discounted prices based on volume. New and upcoming clinics and clinic chains and franchises can take advantage of our attractive financing and leasing options through our <a href=\"/financing\" style=\"color: #0056b3; text-decoration: underline;\">financing partners</a>."
        ),
//        FaqData(
//            "Are software subscriptions included within active lease or rental agreements?",
//            "Yes. For both clinical facilities executing commercial leases and personal patients under home rentals, ongoing active access to the comprehensive digital neurotherapeutics software suite and continuous remote updates are completely integrated into the standard rate structure."
//        )
    )
)
